import type { FrameTime, PropertyDescriptor, SourceContext, SourceTypeDefinition } from '../../core/definitions';
import { BaseSourceRuntime, bool, num } from '../base';
import {
  BAR_COLOURS,
  COUNTER_MODULO,
  encodeStrip,
  flashIndex,
  frameAt,
  movingBoxX,
  PATTERN_REGIONS,
  prng,
  stripHeight,
  TEST_SIGNAL_LAYOUT,
} from './layout';

export const TEST_SIGNAL_KIND = 'diagnostics.test-signal';

const FPS_CHOICES = [24, 25, 30, 50, 60] as const;
const PATTERNS = ['bars', 'counter-strip', 'ramp', 'checker', 'moving-box', 'noise'] as const;
const AUDIO_MODES = ['off', 'pilot', 'pilot+click', 'sweep'] as const;
const MAX_SIDE = 3840;
const PILOT_HZ = 440;
const PILOT_GAIN = 0.03;
const CLICK_HZ = 2000;
const CLICK_GAIN = 0.9;
const SWEEP_LOW_HZ = 100;
const SWEEP_HIGH_HZ = 8000;
const SWEEP_PERIOD_MS = 4000;
/** Holds about 2 minutes of frames at 30 fps plus their flashes and faults. */
export const LOG_CAPACITY = 4096;

export type TestSignalPattern = (typeof PATTERNS)[number];
export type TestSignalAudio = (typeof AUDIO_MODES)[number];

export type TestSignalSettings = {
  width: number;
  height: number;
  /** Frames per second of the source's own clock, or 'compositor' for one frame per composed frame. */
  fps: number | 'compositor';
  pattern: TestSignalPattern;
  /** Frame number and timestamps written into the picture. */
  burnIn: boolean;
  audio: TestSignalAudio;
  /** 0 turns the flash and click off. */
  flashPeriodMs: number;
  seed: number;
};

export type TestSignalLogEntry =
  | { type: 'frame'; counter: number; wallMs: number; perfMs: number }
  | { type: 'flash'; index: number; counter: number; wallMs: number; perfMs: number; audioTime: number; audioState: string }
  | { type: 'fault'; action: string; wallMs: number; perfMs: number; detail?: number };

/** Fixed-size log: the newest `capacity` entries survive, older ones are counted as dropped. */
export class RingLog<T> {
  private readonly items: T[] = [];
  private start = 0;
  dropped = 0;

  constructor(readonly capacity: number) {}

  push(item: T): void {
    if (this.items.length < this.capacity) {
      this.items.push(item);
      return;
    }
    this.items[this.start] = item;
    this.start = (this.start + 1) % this.capacity;
    this.dropped++;
  }

  clear(): void {
    this.items.length = 0;
    this.start = 0;
    this.dropped = 0;
  }

  toArray(): T[] {
    return [...this.items.slice(this.start), ...this.items.slice(0, this.start)];
  }

  get size(): number {
    return this.items.length;
  }
}

/** Repairs any stored bag (old projects, imported bundles) to valid settings. */
export function normalizeTestSignalSettings(raw: unknown): TestSignalSettings {
  const bag = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const fps = bag.fps === 'compositor' ? 'compositor' : (FPS_CHOICES as readonly number[]).includes(bag.fps as number) ? (bag.fps as number) : 30;
  return {
    width: Math.round(num(bag.width, 1280, 16, MAX_SIDE)),
    height: Math.round(num(bag.height, 720, 16, MAX_SIDE)),
    fps,
    pattern: (PATTERNS as readonly unknown[]).includes(bag.pattern) ? (bag.pattern as TestSignalPattern) : 'bars',
    burnIn: bool(bag.burnIn, true),
    audio: (AUDIO_MODES as readonly unknown[]).includes(bag.audio) ? (bag.audio as TestSignalAudio) : 'pilot+click',
    flashPeriodMs: Math.round(num(bag.flashPeriodMs, 2000, 0, 60_000)),
    seed: Math.round(num(bag.seed, 1, 0, 0xffffffff)),
  };
}

function hex([r, g, b]: readonly [number, number, number]): string {
  return `rgb(${r},${g},${b})`;
}

/**
 * Draws one frame. Everything but the burned-in clock text is a function of
 * (settings, n, flash), so the same frame number always gives the same pixels.
 */
export function drawTestFrame(g: CanvasRenderingContext2D, s: TestSignalSettings, n: number, flash: boolean, burnIn: string | null): void {
  const { width: w, height: h } = s;
  const top = stripHeight(h);
  const ph = h - top;
  g.fillStyle = '#000000';
  g.fillRect(0, 0, w, h);

  const region = (r: { x: number; y: number; w: number; h: number }) => ({
    x: Math.round(r.x * w),
    y: top + Math.round(r.y * ph),
    w: Math.round((r.x + r.w) * w) - Math.round(r.x * w),
    h: Math.round((r.y + r.h) * ph) - Math.round(r.y * ph),
  });
  const checker = (r: { x: number; y: number; w: number; h: number }, size: number) => {
    g.fillStyle = '#000000';
    g.fillRect(r.x, r.y, r.w, r.h);
    g.fillStyle = '#ffffff';
    for (let y = 0; y < r.h; y += size) {
      for (let x = ((y / size) & 1) * size; x < r.w; x += size * 2) g.fillRect(r.x + x, r.y + y, Math.min(size, r.w - x), Math.min(size, r.h - y));
    }
  };
  const movingBox = (r: { x: number; y: number; w: number; h: number }) => {
    const bw = Math.max(4, Math.round(r.w * 0.1));
    const bx = r.x + Math.round(movingBoxX(n, bw / r.w) * r.w);
    g.fillStyle = '#ffffff';
    g.fillRect(bx, r.y + Math.round(r.h * 0.2), bw, Math.max(2, Math.round(r.h * 0.6)));
  };
  const whole = { x: 0, y: top, w, h: ph };

  switch (s.pattern) {
    case 'bars': {
      const bars = region(PATTERN_REGIONS.bars);
      BAR_COLOURS.forEach((c, i) => {
        const x0 = bars.x + Math.round((i * bars.w) / BAR_COLOURS.length);
        const x1 = bars.x + Math.round(((i + 1) * bars.w) / BAR_COLOURS.length);
        g.fillStyle = hex(c);
        g.fillRect(x0, bars.y, x1 - x0, bars.h);
      });
      checker(region(PATTERN_REGIONS.checker), PATTERN_REGIONS.checkerPx);
      movingBox(region(PATTERN_REGIONS.track));
      break;
    }
    case 'ramp': {
      const grad = g.createLinearGradient(0, 0, w, 0);
      grad.addColorStop(0, '#000000');
      grad.addColorStop(1, '#ffffff');
      g.fillStyle = grad;
      g.fillRect(whole.x, whole.y, whole.w, whole.h);
      break;
    }
    case 'checker':
      checker(whole, PATTERN_REGIONS.checkerPx);
      break;
    case 'moving-box':
      movingBox(whole);
      break;
    case 'noise': {
      // Blocks rather than pixels keep a 1080p frame cheap; the seed makes the sequence repeatable.
      const rand = prng((s.seed ^ Math.imul(n + 1, 0x9e3779b1)) >>> 0);
      const cols = 64;
      const rows = 36;
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const v = Math.floor(rand() * 256);
          g.fillStyle = `rgb(${v},${v},${v})`;
          const x0 = Math.round((x * w) / cols);
          const y0 = top + Math.round((y * ph) / rows);
          g.fillRect(x0, y0, Math.round(((x + 1) * w) / cols) - x0, top + Math.round(((y + 1) * ph) / rows) - y0);
        }
      }
      break;
    }
    case 'counter-strip':
      break;
  }

  const cells = encodeStrip(n, flash);
  g.fillStyle = '#ffffff';
  for (let i = 0; i < cells.length; i++) {
    if (!cells[i]) continue;
    const x0 = Math.round((i * w) / cells.length);
    g.fillRect(x0, 0, Math.round(((i + 1) * w) / cells.length) - x0, top);
  }

  if (burnIn) {
    const size = Math.max(10, Math.round(h / 24));
    g.font = `${size}px ui-monospace, Menlo, Consolas, monospace`;
    g.textBaseline = 'top';
    const pad = Math.round(size / 3);
    const tw = Math.ceil(g.measureText(burnIn).width);
    g.fillStyle = 'rgba(0,0,0,0.75)';
    g.fillRect(pad, top + pad, tw + pad * 2, size + pad * 2);
    g.fillStyle = '#ffffff';
    g.fillText(burnIn, pad * 2, top + pad * 2);
  }
}

function clickBuffer(context: BaseAudioContext): AudioBuffer {
  const length = Math.round(context.sampleRate * 0.005);
  const buffer = context.createBuffer(1, length, context.sampleRate);
  const data = buffer.getChannelData(0);
  // Starts at full scale: the onset is what a receiver times.
  for (let i = 0; i < length; i++) data[i] = Math.cos((2 * Math.PI * CLICK_HZ * i) / context.sampleRate) * (1 - i / length) * CLICK_GAIN;
  return buffer;
}

export class TestSignalRuntime extends BaseSourceRuntime<TestSignalSettings> {
  private settings: TestSignalSettings;
  private readonly canvas = document.createElement('canvas');
  private readonly g: CanvasRenderingContext2D | null;
  private readonly entries = new RingLog<TestSignalLogEntry>(LOG_CAPACITY);
  /** performance.now() at which frame 0 of the own clock started. */
  private clockStart = performance.now();
  private anchored = false;
  private compositorFrames = 0;
  private lastDrawn = -1;
  private lastFlash = 0;
  private showing = false;
  private frozenUntil = 0;
  private dropRemaining = 0;
  private mutedUntil = 0;
  private muteTimer: ReturnType<typeof setTimeout> | null = null;

  private output: GainNode | null = null;
  private pilot: OscillatorNode | null = null;
  private pilotGain: GainNode | null = null;
  private click: AudioBuffer | null = null;
  private readonly clicks = new Set<AudioBufferSourceNode>();

  constructor(ctx: SourceContext, settings: TestSignalSettings) {
    super(ctx);
    this.settings = normalizeTestSignalSettings(settings);
    this.g = this.canvas.getContext('2d', { alpha: false });
    if (!this.g) {
      this.fail('unsupported', '2D canvas is unavailable');
      return;
    }
    this.resize();
    this.drawFrame(0, performance.now());
    this.publishCanvas(this.canvas);
    this.buildAudio();
    this.setStatus('live');
  }

  update(next: TestSignalSettings): void {
    if (this.destroyed) return;
    const prev = this.settings;
    this.settings = normalizeTestSignalSettings(next);
    if (!this.g) return;
    if (prev.width !== this.settings.width || prev.height !== this.settings.height) {
      this.resize();
      this.drawFrame(Math.max(0, this.lastDrawn), performance.now());
      this.publishCanvas(this.canvas);
    }
    if (prev.audio !== this.settings.audio) this.buildAudio();
    if (prev.fps !== this.settings.fps) this.anchored = false;
  }

  setShowing(showing: boolean): void {
    this.showing = showing;
  }

  tick(_time: FrameTime): void {
    if (this.destroyed || !this.g) return;
    this.compositorFrames++;
    const now = performance.now();
    const fps = this.settings.fps;
    if (!this.anchored && fps !== 'compositor') {
      // Put composed frames in the middle of the clock's frame slots: at equal rates, timer jitter of up
      // to half a frame then never shows a frame twice or skips one, so any repeat or skip is real.
      this.anchored = true;
      this.clockStart = now - 500 / fps;
    }
    const n = (fps === 'compositor' ? this.compositorFrames : frameAt(now - this.clockStart, fps)) % COUNTER_MODULO;
    if (this.settings.audio === 'sweep' && this.pilot) this.pilot.frequency.setValueAtTime(this.sweepHz(now), this.ctx.audioContext.currentTime);
    if (n === this.lastDrawn) return;
    if (now < this.frozenUntil) return;
    if (this.dropRemaining > 0) {
      // A dropped frame is one the clock passed without a picture: the next drawn counter jumps over it.
      this.dropRemaining -= Math.max(1, n - Math.max(this.lastDrawn, 0));
      this.lastDrawn = n;
      return;
    }
    this.drawFrame(n, now);
    this.touchVideo();
  }

  /**
   * - `freeze` `{ ms }`: no new pictures for `ms` (default 3000); the clock keeps running, so the
   *   counter jumps when pictures resume.
   * - `drop-frames` `{ count }`: skip the next `count` frames of the clock (default 5).
   * - `mute-audio` `{ ms }`: silence the output for `ms` (default 3000).
   * - `read-log` `{ receive(entries, dropped) }`: hand the bounded log to a test.
   * - `clear-log`.
   */
  invoke(action: string, args?: unknown): void {
    if (this.destroyed) return;
    const a = args && typeof args === 'object' ? (args as Record<string, unknown>) : {};
    const now = performance.now();
    switch (action) {
      case 'freeze': {
        const ms = num(a.ms, 3000, 0, 600_000);
        this.frozenUntil = now + ms;
        this.fault(action, ms);
        break;
      }
      case 'drop-frames': {
        const count = Math.round(num(a.count, 5, 1, 10_000));
        this.dropRemaining += count;
        this.fault(action, count);
        break;
      }
      case 'mute-audio': {
        const ms = num(a.ms, 3000, 0, 600_000);
        this.mutedUntil = now + ms;
        this.applyGain();
        if (this.muteTimer) clearTimeout(this.muteTimer);
        this.muteTimer = setTimeout(() => {
          this.muteTimer = null;
          this.applyGain();
        }, ms);
        this.fault(action, ms);
        break;
      }
      case 'read-log': {
        if (typeof a.receive === 'function') (a.receive as (entries: TestSignalLogEntry[], dropped: number) => void)(this.entries.toArray(), this.entries.dropped);
        break;
      }
      case 'clear-log':
        this.entries.clear();
        break;
    }
    this.emit();
  }

  getLiveInfo(): Record<string, string> {
    const now = performance.now();
    const s = this.settings;
    return {
      frame: String(Math.max(0, this.lastDrawn)),
      fps: s.fps === 'compositor' ? 'compositor' : String(s.fps),
      resolution: `${s.width}×${s.height}`,
      fault: now < this.frozenUntil ? 'video frozen' : now < this.mutedUntil ? 'audio muted' : this.dropRemaining > 0 ? 'dropping frames' : 'none',
      log: `${this.entries.size}/${LOG_CAPACITY}`,
    };
  }

  /** A frozen test signal must raise the alert, so a freeze does not count as a legitimate gap. */
  expectsContinuousFrames(): boolean {
    return this.showing && this.getStatus().state === 'live';
  }

  private fault(action: string, detail: number): void {
    this.entries.push({ type: 'fault', action, wallMs: Date.now(), perfMs: performance.now(), detail });
    this.log('info', `fault injected: ${action} ${detail}`);
  }

  private resize(): void {
    this.canvas.width = this.settings.width;
    this.canvas.height = this.settings.height;
  }

  private sweepHz(now: number): number {
    const phase = ((now - this.clockStart) % SWEEP_PERIOD_MS) / SWEEP_PERIOD_MS;
    return SWEEP_LOW_HZ * (SWEEP_HIGH_HZ / SWEEP_LOW_HZ) ** phase;
  }

  private drawFrame(n: number, now: number): void {
    const s = this.settings;
    const fps = s.fps === 'compositor' ? 30 : s.fps;
    const k = flashIndex(n, fps, s.flashPeriodMs);
    const flash = k >= 0;
    const elapsed = now - this.clockStart;
    const text = s.burnIn ? `#${String(n).padStart(7, '0')}  t=${(elapsed / 1000).toFixed(3)}s  ${new Date().toISOString().slice(11, 23)}` : null;
    drawTestFrame(this.g!, s, n, flash, text);
    this.lastDrawn = n;
    this.entries.push({ type: 'frame', counter: n, wallMs: Date.now(), perfMs: now });
    if (flash && k !== this.lastFlash) {
      this.lastFlash = k;
      // Started in the same tick that first paints the flash cells: the receiver measures the gap between them.
      this.startClick();
      const audio = this.ctx.audioContext;
      this.entries.push({ type: 'flash', index: k, counter: n, wallMs: Date.now(), perfMs: now, audioTime: audio.currentTime, audioState: audio.state });
    }
  }

  private startClick(): void {
    if (!this.output || !this.click || this.settings.audio !== 'pilot+click') return;
    const node = this.ctx.audioContext.createBufferSource();
    node.buffer = this.click;
    node.connect(this.output);
    node.onended = () => {
      node.disconnect();
      this.clicks.delete(node);
    };
    this.clicks.add(node);
    node.start();
  }

  private applyGain(): void {
    if (!this.output) return;
    const muted = performance.now() < this.mutedUntil;
    this.output.gain.setValueAtTime(muted ? 0 : 1, this.ctx.audioContext.currentTime);
  }

  private buildAudio(): void {
    this.releaseAudio();
    if (this.settings.audio === 'off') {
      this.setAudio(null);
      return;
    }
    const context = this.ctx.audioContext;
    this.output = context.createGain();
    this.pilot = context.createOscillator();
    this.pilotGain = context.createGain();
    this.pilot.frequency.value = this.settings.audio === 'sweep' ? SWEEP_LOW_HZ : PILOT_HZ;
    this.pilotGain.gain.value = this.settings.audio === 'sweep' ? 0.25 : PILOT_GAIN;
    this.pilot.connect(this.pilotGain).connect(this.output);
    this.pilot.start();
    this.click = clickBuffer(context);
    this.applyGain();
    this.setAudio(this.output);
  }

  private releaseAudio(): void {
    for (const node of this.clicks) {
      try {
        node.stop();
      } catch {
        // Already ended.
      }
      node.disconnect();
    }
    this.clicks.clear();
    if (this.pilot) {
      try {
        this.pilot.stop();
      } catch {
        // Never started.
      }
      this.pilot.disconnect();
    }
    this.pilotGain?.disconnect();
    this.output?.disconnect();
    this.pilot = null;
    this.pilotGain = null;
    this.output = null;
  }

  protected release(): void {
    if (this.muteTimer) clearTimeout(this.muteTimer);
    this.muteTimer = null;
    this.releaseAudio();
    this.canvas.width = 0;
    this.canvas.height = 0;
  }
}

export const testSignalDefinition: SourceTypeDefinition<TestSignalSettings> = {
  kind: TEST_SIGNAL_KIND,
  displayName: 'Test signal (diagnostics)',
  caps: { video: true, audio: true },
  category: 'utility',
  diagnostic: true,
  settingsVersion: 1,
  migrate: (settings) => normalizeTestSignalSettings(settings),
  defaults: () => normalizeTestSignalSettings({}),
  properties: (): PropertyDescriptor[] => [
    { key: 'about', label: 'About', type: 'info', description: 'Deterministic picture and sound for checking frame pacing, freezes and A/V sync. Only listed in diagnostics mode.' },
    { key: 'frame', label: 'Frame', type: 'info', liveKey: 'frame' },
    { key: 'fault', label: 'Injected fault', type: 'info', liveKey: 'fault' },
    { key: 'width', label: 'Width', type: 'number', min: 16, max: MAX_SIDE, step: 1, unit: 'px', applyOn: 'commit' },
    { key: 'height', label: 'Height', type: 'number', min: 16, max: MAX_SIDE, step: 1, unit: 'px', applyOn: 'commit' },
    {
      key: 'fps',
      label: 'Frame rate',
      type: 'select',
      options: [...FPS_CHOICES.map((f) => ({ value: f, label: `${f} fps` })), { value: 'compositor', label: 'One per composed frame' }],
    },
    {
      key: 'pattern',
      label: 'Pattern',
      type: 'select',
      options: [
        { value: 'bars', label: 'Colour bars, moving box and checkerboard' },
        { value: 'counter-strip', label: 'Counter strip only' },
        { value: 'ramp', label: 'Grey ramp' },
        { value: 'checker', label: 'Checkerboard' },
        { value: 'moving-box', label: 'Moving box' },
        { value: 'noise', label: 'Seeded noise' },
      ],
    },
    { key: 'seed', label: 'Noise seed', type: 'number', min: 0, max: 0xffffffff, step: 1, visibleWhen: (s) => s.pattern === 'noise' },
    { key: 'burnIn', label: 'Burn in frame number and time', type: 'boolean' },
    {
      key: 'audio',
      label: 'Audio',
      type: 'select',
      options: [
        { value: 'off', label: 'Off' },
        { value: 'pilot', label: '440 Hz pilot tone' },
        { value: 'pilot+click', label: 'Pilot tone and sync click' },
        { value: 'sweep', label: 'Frequency sweep' },
      ],
    },
    { key: 'flashPeriodMs', label: 'Sync flash period', type: 'duration', min: 0, max: 60_000 },
    { key: 'freeze', label: 'Freeze video for 3 s', type: 'button', action: 'freeze', variant: 'danger' },
    { key: 'drop', label: 'Drop 5 frames', type: 'button', action: 'drop-frames' },
    { key: 'mute', label: 'Mute audio for 3 s', type: 'button', action: 'mute-audio' },
  ],
  create: (ctx, settings) => new TestSignalRuntime(ctx, settings),
};
